// app/(whatever)/upload-avatars/page.tsx
"use client";

import { useRef, useState, useEffect } from "react";
import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import clsx from "clsx";
import { FiUploadCloud } from "react-icons/fi";
import { uploadZipMultipart } from "@/lib/multipartUpload";
import {
  START_PROCESS_AVATARS_MUTATION,
} from "@/graphql/mutations/uploadMutations";
import { JOB_STATUS_QUERY } from "@/graphql/queries/uploadQueries";

export default function UploadPlayerAvatarsPage() {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);

  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);

  const [taskId, setTaskId] = useState<string | null>(null);
  const [processingPct, setProcessingPct] = useState(0);

  const client = useApolloClient();
  const [startProcess] = useMutation(START_PROCESS_AVATARS_MUTATION);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setFile(e.target.files?.[0] || null);

  const clearFileInput = () => {
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleClick = () => fileInputRef.current?.click();

  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);
    setUploadPct(0);
    setProcessingPct(0);
    setTaskId(null);

    try {
      // 1) Upload to Spaces with multipart + live progress
      const { key } = await uploadZipMultipart(
        client,
        file,
        (done, total) => setUploadPct(Math.round((done / total) * 100)),
        4,
        "avatars" // << namespace avatars
      );

      // 2) Kick off backend processing task
      const res = await startProcess({ variables: { key } });
      setTaskId(res.data.startProcessPlayerAvatarsZip.taskId);
    } catch (err) {
      console.error(err);
    } finally {
      setUploading(false);
      clearFileInput();
    }
  };

  // Poll job status while task is active
  const { data: statusData, startPolling, stopPolling } = useQuery(
    JOB_STATUS_QUERY,
    {
      variables: { taskId },
      skip: !taskId,
      pollInterval: 1500,
      fetchPolicy: "no-cache",
    }
  );

  useEffect(() => {
    if (!taskId) return;
    startPolling(1500);
    return () => stopPolling();
  }, [taskId, startPolling, stopPolling]);

  useEffect(() => {
    const s = statusData?.jobStatus;
    if (!s) return;
    if (typeof s.percent === "number") setProcessingPct(s.percent);
    if (s.state === "SUCCESS" || s.state === "FAILURE") {
      stopPolling();
    }
  }, [statusData, stopPolling]);

  const job = statusData?.jobStatus;
  const finished = job?.state === "SUCCESS" || job?.state === "FAILURE";

  const processedFallback =
    job?.processed ??
    (job?.result
      ? (job.result.successfulUploads?.length || 0) +
        (job.result.failedUploads?.length || 0)
      : 0);
  const totalFallback = job?.total ?? processedFallback;

  return (
    <div className="mx-auto mt-4 w-full px-4">
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
        {/* LEFT: Upload Form */}
        <div className="rounded-2xl border border-slate-300 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-slate-900">
          <h1 className="mb-4 text-2xl font-bold text-slate-900 dark:text-slate-100">
            Upload Player Avatars (ZIP)
          </h1>

          <div className="relative mb-4">
            <div
              className={clsx(
                "flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-6 transition-colors duration-150",
                file
                  ? "border-brand bg-brand/10 hover:bg-brand/20 dark:bg-brand/10 dark:hover:bg-brand/20"
                  : "border-slate-300 hover:bg-slate-100 dark:border-slate-600 dark:hover:bg-slate-800"
              )}
              onClick={handleClick}
            >
              <FiUploadCloud className="h-10 w-10 text-slate-600 dark:text-slate-300" />
              <p className="text-sm text-slate-700 dark:text-slate-400">
                {file
                  ? file.name
                  : "Click to select a .zip of PNGs named by shield number (e.g., 1.png, 2.png)."}
              </p>
            </div>

            {file && (
              <button
                onClick={clearFileInput}
                className="absolute right-2 top-2 cursor-pointer text-sm text-slate-600 hover:text-red-500 dark:text-slate-300"
                title="Clear file"
                type="button"
              >
                ×
              </button>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept=".zip"
              onChange={handleFileChange}
              className="hidden"
            />
          </div>

          <button
            disabled={!file || uploading}
            onClick={handleUpload}
            className={clsx(
              "w-full rounded-md px-4 py-2 font-medium transition-colors",
              uploading
                ? "cursor-not-allowed bg-slate-300 text-slate-500 dark:bg-slate-800 dark:text-slate-500"
                : "cursor-pointer bg-brand text-white hover:bg-brand/90"
            )}
          >
            {uploading ? "Uploading..." : "Upload"}
          </button>

          {/* Upload progress */}
          {uploading && (
            <div className="mt-3 h-2 w-full rounded bg-slate-200 dark:bg-slate-700">
              <div
                className="h-2 rounded bg-brand"
                style={{ width: `${uploadPct}%` }}
              />
            </div>
          )}

          {/* Processing progress */}
          {taskId && (
            <div className="mt-4">
              <p className="mb-2 text-sm text-slate-700 dark:text-slate-300">
                Processing ZIP {job ? `— ${job.percent ?? 0}%` : ""}
              </p>
              <div className="h-2 w-full rounded bg-slate-200 dark:bg-slate-700">
                <div
                  className="h-2 rounded bg-brand"
                  style={{ width: `${processingPct}%` }}
                />
              </div>
              {job && (
                <p className="mt-2 text-xs text-slate-600 dark:text-slate-400">
                  {processedFallback}/{totalFallback} processed • ok{" "}
                  {job.successful ?? 0} • failed {job.failed ?? 0}
                </p>
              )}
            </div>
          )}

          {/* Results */}
          {finished && job?.result && (
            <div className="mt-4 space-y-3">
              {job.result.successfulUploads?.length > 0 && (
                <div className="rounded-lg bg-green-100 p-4 text-green-800 dark:bg-green-900/20 dark:text-green-400">
                  <p className="mb-1 font-semibold">Success</p>
                  <p className="text-sm">
                    Updated avatars for:{" "}
                    {job.result.successfulUploads.join(", ")}
                  </p>
                </div>
              )}
              {job.result.failedUploads?.length > 0 && (
                <div className="rounded-lg bg-yellow-100 p-4 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400">
                  <p className="mb-1 font-semibold">Failed</p>
                  <p className="text-sm">
                    No matching shield/player for:{" "}
                    {job.result.failedUploads.join(", ")}
                  </p>
                </div>
              )}
              {job.result.error && (
                <div className="rounded-lg bg-red-100 p-4 text-red-800 dark:bg-red-900/20 dark:text-red-400">
                  <p className="mb-1 font-semibold">Error</p>
                  <p className="text-sm">{job.result.error}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* RIGHT: Docs Panel */}
        <div className="rounded-2xl border border-slate-300 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-slate-900">
          <h2 className="mb-4 text-2xl font-bold text-slate-900 dark:text-slate-100">
            How to Get Your Avatars ZIP
          </h2>
          <p className="text-sm leading-relaxed text-slate-700 dark:text-slate-400">
            1. Visit the shared Google Drive folder{" "}
            <a
              href="https://drive.google.com/drive/folders/1EiUNWX9M5lEIpzz8Osl09OjASHwO8Cqa?usp=sharing"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand font-medium hover:underline"
            >
              here
            </a>
            .
            <br />
            2. Find the <strong>“Portal Export”</strong> folder.
            <br />
            3. Right click it, then choose <strong>“Download”</strong>.
            <br />
            4. You’ll get a ZIP with PNGs named by shield number (e.g.,
            <code>17.png</code>). Upload it here to set player avatars.
          </p>
        </div>
      </div>
    </div>
  );
}
